import React, { useCallback } from "react";
import { Alert, Button, Form, Input, InputNumber, Space, Switch, Typography } from "antd";
import { SectionPanelProps } from "./SectionTypes";
import { useJsonSection } from "./useJsonSection";
import {
    ClientConfig,
    DEFAULT_CHECKIN_DUPLICATE_WINDOW_MINUTES,
    MAX_CHECKIN_DUPLICATE_WINDOW_MINUTES,
    MIN_CHECKIN_DUPLICATE_WINDOW_MINUTES,
} from "../../../model/client/ClientSettings";
import { CopyFromClientButton } from "./CopyFromClientModal";
import {
    BirthdayMessageConfig,
    parseBirthdayMessage,
} from "../../../components/membership/profile/birthdayMessage";

const { Text } = Typography;

export const CHECKIN_BIRTHDAY_KEY = 'checkin.birthdayMessage';
export const CHECKIN_DUPLICATE_WINDOW_KEY = 'checkin.duplicateWindowMinutes';

// Everything this panel edits, loaded and saved as one unit. The birthday greeting is an
// opaque JSON blob; the window is a whole number the API itself reads — different storage
// types behind the same Save button.
interface CheckinSectionValue {
    birthday: BirthdayMessageConfig;
    duplicateWindowMinutes: number;
}

const isValidWindow = (minutes: number): boolean =>
    Number.isInteger(minutes) &&
    minutes >= MIN_CHECKIN_DUPLICATE_WINDOW_MINUTES &&
    minutes <= MAX_CHECKIN_DUPLICATE_WINDOW_MINUTES;

// Settings panel for check-in behaviour: the birthday greeting
// (checkin.birthdayMessage) and the repeat-scan window used by the check-in report
// (checkin.duplicateWindowMinutes).
export const CheckinSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const extract = useCallback(
        (config: ClientConfig | undefined): CheckinSectionValue => ({
            birthday: parseBirthdayMessage(config?.settings.checkin?.birthdayMessageJson),
            duplicateWindowMinutes:
                config?.settings.checkin?.duplicateWindowMinutes ?? DEFAULT_CHECKIN_DUPLICATE_WINDOW_MINUTES,
        }),
        [],
    );
    const { clientId, value, setValue, initial, loadError, saveError, saving, save, reload } =
        useJsonSection<CheckinSectionValue>(extract);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-checkin-load-error" />;
    }
    if (value === null || initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    const dirty = JSON.stringify(initial) !== JSON.stringify(value);
    const valid = value.birthday.template.trim().length > 0 && isValidWindow(value.duplicateWindowMinutes);
    // The window row only goes up when it actually changed, so a club that has never set one
    // keeps an absent row (and moves with the product default) rather than being pinned to
    // today's 5 by someone editing the birthday greeting.
    const windowChanged = value.duplicateWindowMinutes !== initial.duplicateWindowMinutes;
    const handleSave = () =>
        save(
            [
                { key: CHECKIN_BIRTHDAY_KEY, value: JSON.stringify(value.birthday), valueType: 'json' },
                ...(windowChanged
                    ? [{
                        key: CHECKIN_DUPLICATE_WINDOW_KEY,
                        value: String(value.duplicateWindowMinutes),
                        valueType: 'number' as const,
                    }]
                    : []),
            ],
            value,
        );

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <CopyFromClientButton targetClientId={clientId} groups={['checkin']} onCopied={reload} />
            <Form layout="vertical" data-testid="settings-checkin-form">
                <Form.Item label="Show a birthday greeting on check-in">
                    <Switch
                        data-testid="settings-checkin-enabled"
                        checked={value.birthday.enabled}
                        onChange={(checked) =>
                            setValue({ ...value, birthday: { ...value.birthday, enabled: checked } })
                        }
                    />
                </Form.Item>
                <Form.Item
                    label="Greeting template"
                    required
                    help="Use {{firstName}} to insert the member's first name."
                >
                    <Input.TextArea
                        data-testid="settings-checkin-template"
                        rows={2}
                        value={value.birthday.template}
                        onChange={(e) =>
                            setValue({ ...value, birthday: { ...value.birthday, template: e.target.value } })
                        }
                    />
                </Form.Item>
                <Form.Item
                    label="Repeat check-in window (minutes)"
                    help={
                        'Reporting only. If a member scans again within this many minutes of their last '
                        + 'scan, the check-in report counts it as the same visit — so a card tapped twice '
                        + 'at reception is one check-in, not two. Every scan is still recorded, and nobody '
                        + 'is ever turned away. Set to 0 to count every scan.'
                    }
                >
                    <InputNumber
                        data-testid="settings-checkin-duplicate-window"
                        inputMode="numeric"
                        min={MIN_CHECKIN_DUPLICATE_WINDOW_MINUTES}
                        max={MAX_CHECKIN_DUPLICATE_WINDOW_MINUTES}
                        precision={0}
                        value={value.duplicateWindowMinutes}
                        onChange={(next) =>
                            setValue({
                                ...value,
                                duplicateWindowMinutes:
                                    typeof next === 'number' ? next : value.duplicateWindowMinutes,
                            })
                        }
                    />
                </Form.Item>
            </Form>
            {saveError && <Alert type="error" message={saveError} data-testid="settings-checkin-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-checkin-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-checkin-discard-btn"
                    onClick={() => setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
